import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Chart, registerables } from 'chart.js';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { getDateRange, formatDate, validateDateRange } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { monitorQuery } from '../utils/queryMonitoring';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import CompactChart from '../components/common/CompactChart';
import { useUserRole } from '../hooks/useUserRole';
import { usePermissions } from '../hooks/usePermissions';
import { canViewAllData, canViewAllReports, getRbacMatchNames, FEATURE_PERMISSIONS } from '../utils/permissions';
import { Link } from 'react-router-dom';
import ExportButton from '../components/common/ExportButton';
import { useUrlState } from '../hooks/useUrlState';
import { exportData, exportMultiSectionData } from '../utils/exportUtils';

Chart.register(...registerables);

const TaskReportsPage = ({ user }) => {
    const { currentUser } = useAuth();
    const userRole = useUserRole(user);
    const { can } = usePermissions(user);
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [range, setRange] = useUrlState('range', "30");
    const [customStart, setCustomStart] = useUrlState('customStart', "");
    const [customEnd, setCustomEnd] = useUrlState('customEnd', "");
    const [selectedAssignees, setSelectedAssignees] = useUrlState('selectedAssignees', []);
    const [pendingAssignees, setPendingAssignees] = useState([]);
    const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
    const assigneeDropdownRef = useRef(null);
    
    const [selectedPriorities, setSelectedPriorities] = useUrlState('selectedPriorities', []);
    const [pendingPriorities, setPendingPriorities] = useState([]);
    const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
    const priorityDropdownRef = useRef(null);
    
    // Chart configuration state
    const [statusChartConfig, setStatusChartConfig] = useState(null);
    const [assigneeChartConfig, setAssigneeChartConfig] = useState(null);
    const [completionTrendChartConfig, setCompletionTrendChartConfig] = useState(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
                setShowAssigneeDropdown(false);
            }
            if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target)) {
                setShowPriorityDropdown(false);
            }
        };

        if (showAssigneeDropdown || showPriorityDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showAssigneeDropdown, showPriorityDropdown]);

    // Initialize pendingAssignees when dropdown opens
    useEffect(() => {
        if (showAssigneeDropdown) {
            setPendingAssignees(selectedAssignees);
        }
    }, [showAssigneeDropdown, selectedAssignees]);

    // Initialize pendingPriorities when dropdown opens
    useEffect(() => {
        if (showPriorityDropdown) {
            setPendingPriorities(selectedPriorities);
        }
    }, [showPriorityDropdown, selectedPriorities]);



    // Apply assignee filter changes
    const handleApplyAssigneeFilter = () => {
        if (pendingAssignees.length === allAssignees.length) {
            setSelectedAssignees([]);
        } else {
            setSelectedAssignees(pendingAssignees);
        }
        setShowAssigneeDropdown(false);
    };

    // Cancel assignee filter changes
    const handleCancelAssigneeFilter = () => {
        setPendingAssignees(selectedAssignees);
        setShowAssigneeDropdown(false);
    };

    // Apply priority filter changes
    const handleApplyPriorityFilter = () => {
        if (pendingPriorities.length === 3) {
            setSelectedPriorities([]);
        } else {
            setSelectedPriorities(pendingPriorities);
        }
        setShowPriorityDropdown(false);
    };

    // Cancel priority filter changes
    const handleCancelPriorityFilter = () => {
        setPendingPriorities(selectedPriorities);
        setShowPriorityDropdown(false);
    };

    const hasActiveFilters = useMemo(() => {
        return range !== '30' || customStart || customEnd || selectedAssignees.length > 0 || selectedPriorities.length > 0;
    }, [range, customStart, customEnd, selectedAssignees, selectedPriorities]);

    const handleClearFilters = () => {
        setRange('30');
        setCustomStart('');
        setCustomEnd('');
        setSelectedAssignees([]);
        setPendingAssignees([]);
        setSelectedPriorities([]);
        setPendingPriorities([]);
    };

    useEffect(() => {
        if (!user || !user.uid) return;
        // Wait for userRole to load before filtering
        if (userRole === undefined || userRole === null) return;

        const fetchData = async () => {
            setLoading(true);
            try {

                // Validate date range before fetching
                const validation = validateDateRange(range, customStart, customEnd);
                if (!validation.isValid) {
                    setToast({ message: validation.error, type: 'error' });
                    setLoading(false);
                    return;
                }

                // Fetch tasks
                const tasksSnap = await monitorQuery('fetch-tasks-task-reports', () =>
                    getDocs(collection(db, collectionPath('tasks')))
                );
                let tasksData = tasksSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                // RBAC: Non-admin users only see tasks assigned to them
                // Admins see ALL tasks
                console.log('[TaskReports RBAC]', {
                    userRole,
                    userDisplayName: user?.displayName,
                    userUid: user?.uid,
                    totalTasks: tasksData.length,
                    isAdmin: canViewAllData(user)
                });
                
                if (!canViewAllReports(user) && !can(FEATURE_PERMISSIONS.TASK_VIEW_ALL)) {
                    // Non-admin: Filter to only show assigned tasks (multi-name matching)
                    const matchNames = getRbacMatchNames(user);
                    const matchSet = new Set(matchNames);
                    const beforeFilter = tasksData.length;
                    tasksData = tasksData.filter(task => 
                        matchSet.has(task.assignedTo?.toLowerCase().trim()) || 
                        task.uid === user.uid
                    );
                    console.log('[TaskReports RBAC] Non-admin filtered:', {
                        before: beforeFilter,
                        after: tasksData.length,
                        matchNames,
                        sampleTask: tasksData[0]
                    });
                } else {
                    console.log('[TaskReports RBAC] Admin - showing all tasks:', {
                        totalTasks: tasksData.length,
                        userRole
                    });
                }
                
                setTasks(tasksData);

                // Fetch users
                const usersSnap = await monitorQuery('fetch-users-task-reports', () =>
                    getDocs(collection(db, collectionPath('users')))
                );
                const usersData = usersSnap.docs.map(doc => ({
                    uid: doc.id,
                    ...doc.data()
                }));
                setUsers(usersData);

                setLoading(false);
            } catch (error) {
                console.error('[TaskReports] Error fetching data:', error);
                
                // Check if it's a collection not found error
                if (error.message?.includes('not found') || error.code === 'not-found') {
                    console.warn('[TaskReports] Tasks collection may not exist yet');
                    setTasks([]);
                    setLoading(false);
                } else {
                    setToast({ message: `Error loading data: ${error.message}`, type: 'error' });
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [user?.uid, userRole, can]); // Include userRole in dependencies

    // Date range filtering
    const { start: filterStart, end: filterEnd } = useMemo(() => 
        getDateRange(range, customStart, customEnd), 
        [range, customStart, customEnd]
    );

    // Filter tasks by date range (using createdAt)
    const filteredTasks = useMemo(() => {
        const filtered = tasks.filter(task => {
            const taskDate = task.createdAt?.toDate?.() 
                ? task.createdAt.toDate() 
                : task.createdAt 
                    ? new Date(task.createdAt)
                    : null;
            
            if (!taskDate) {
                return false;
            }
            return taskDate >= filterStart && taskDate <= filterEnd;
        });
        
        return filtered;
    }, [tasks, filterStart, filterEnd]);

    // Filter by selected assignees
    const filteredTasksByAssignee = useMemo(() => {
        if (selectedAssignees.length === 0) return filteredTasks;

        return filteredTasks.filter(task => 
            selectedAssignees.includes(task.assignedTo || 'Unassigned')
        );
    }, [filteredTasks, selectedAssignees]);

    // Filter by selected priorities
    const filteredTasksByPriority = useMemo(() => {
        if (selectedPriorities.length === 0) return filteredTasksByAssignee;

        return filteredTasksByAssignee.filter(task => 
            selectedPriorities.includes(task.priority || 'Medium')
        );
    }, [filteredTasksByAssignee, selectedPriorities]);

    // Get list of all assignees
    const allAssignees = useMemo(() => {
        const assigneeSet = new Set();
        
        tasks.forEach(task => {
            assigneeSet.add(task.assignedTo || 'Unassigned');
        });
        
        const assigneeList = Array.from(assigneeSet).map(name => ({ name }));
        return assigneeList.sort((a, b) => {
            if (a.name === 'Unassigned') return 1;
            if (b.name === 'Unassigned') return -1;
            return a.name.localeCompare(b.name);
        });
    }, [tasks]);

    // Calculate task metrics
    const taskMetrics = useMemo(() => {
        const metrics = {
            total: filteredTasksByPriority.length,
            byStatus: {
                open: 0,
                overdue: 0,
                completed: 0,
                closed: 0
            },
            byPriority: {
                High: 0,
                Medium: 0,
                Low: 0
            },
            overdue: 0,
            completedOnTime: 0
        };

        const now = new Date();

        filteredTasksByPriority.forEach(task => {
            const status = (task.status || 'open').toLowerCase();
            const priority = task.priority || 'Medium';
            
            // Count by priority
            metrics.byPriority[priority] = (metrics.byPriority[priority] || 0) + 1;
            
            // Check if overdue first (for open tasks)
            if (status === 'open' && task.dueDate) {
                const dueDate = task.dueDate?.toDate?.() 
                    ? task.dueDate.toDate() 
                    : new Date(task.dueDate);
                if (dueDate < now) {
                    metrics.byStatus.overdue++;
                    metrics.overdue++;
                } else {
                    metrics.byStatus.open++;
                }
            } else {
                // For completed/closed tasks, use status as-is
                metrics.byStatus[status] = (metrics.byStatus[status] || 0) + 1;
            }

            // Check if completed on time
            if (status === 'completed' && task.dueDate && task.lastUpdated) {
                const dueDate = task.dueDate?.toDate?.() 
                    ? task.dueDate.toDate() 
                    : new Date(task.dueDate);
                const completedDate = task.lastUpdated?.toDate?.() 
                    ? task.lastUpdated.toDate() 
                    : new Date(task.lastUpdated);
                if (completedDate <= dueDate) {
                    metrics.completedOnTime++;
                }
            }
        });

        return metrics;
    }, [filteredTasksByPriority]);

    // Tasks by assignee
    const tasksByAssignee = useMemo(() => {
        const assigneeMap = new Map();

        filteredTasksByPriority.forEach(task => {
            const assignee = task.assignedTo || 'Unassigned';
            if (!assigneeMap.has(assignee)) {
                assigneeMap.set(assignee, {
                    name: assignee,
                    total: 0,
                    open: 0,
                    overdue: 0,
                    completed: 0,
                    closed: 0
                });
            }

            const data = assigneeMap.get(assignee);
            data.total++;
            
            // Normalize status to lowercase
            const status = (task.status || 'open').toLowerCase();
            
            // Check if overdue
            const now = new Date();
            if (status === 'open' && task.dueDate) {
                const dueDate = task.dueDate?.toDate?.() 
                    ? task.dueDate.toDate() 
                    : new Date(task.dueDate);
                if (dueDate < now) {
                    data.overdue++;
                } else {
                    data.open++;
                }
            } else {
                data[status] = (data[status] || 0) + 1;
            }
        });

        return Array.from(assigneeMap.values()).sort((a, b) => b.total - a.total);
    }, [filteredTasksByPriority]);

    // Overdue tasks
    const overdueTasks = useMemo(() => {
        const now = new Date();

        return filteredTasksByPriority
            .filter(task => {
                const status = (task.status || 'open').toLowerCase();
                if (status !== 'open') return false;
                if (!task.dueDate) return false;

                const dueDate = task.dueDate?.toDate?.() 
                    ? task.dueDate.toDate() 
                    : new Date(task.dueDate);
                return dueDate < now;
            })
            .sort((a, b) => {
                const dateA = a.dueDate?.toDate?.() ? a.dueDate.toDate() : new Date(a.dueDate);
                const dateB = b.dueDate?.toDate?.() ? b.dueDate.toDate() : new Date(b.dueDate);
                return dateA - dateB;
            });
    }, [filteredTasksByPriority]);

    // Task completion trend
    const completionTrend = useMemo(() => {
        const rangeDays = Math.ceil((filterEnd - filterStart) / (1000 * 60 * 60 * 24));
        const useMonthly = rangeDays > 90;

        // Group completed tasks by date
        const dateMap = new Map();
        
        const completedFilteredTasks = filteredTasksByPriority.filter(task => {
            const isCompleted = task.status === 'completed' || task.status === 'Completed';
            const hasLastUpdated = !!task.lastUpdated;
            const hasCreatedAt = !!task.createdAt;
            
            // Include completed tasks that have either lastUpdated or createdAt
            return isCompleted && (hasLastUpdated || hasCreatedAt);
        });

        completedFilteredTasks.forEach(task => {
                // Use lastUpdated if available, otherwise fall back to createdAt
                let completedDate;
                
                if (task.lastUpdated?.toDate) {
                    completedDate = task.lastUpdated.toDate();
                } else if (task.lastUpdated) {
                    completedDate = new Date(task.lastUpdated);
                } else if (task.createdAt?.toDate) {
                    completedDate = task.createdAt.toDate();
                } else if (task.createdAt) {
                    completedDate = new Date(task.createdAt);
                }

                if (completedDate) {
                    const dateKey = useMonthly
                        ? `${completedDate.getFullYear()}-${String(completedDate.getMonth() + 1).padStart(2, '0')}`
                        : completedDate.toISOString().split('T')[0];

                    dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + 1);
                }
            });

        // Create complete date range with zero values
        const allDates = [];
        const currentDate = new Date(filterStart);
        const endDate = new Date(filterEnd);
        
        // Normalize dates to avoid time precision issues
        currentDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999); // Include the full end date
        
        if (useMonthly) {
            // Generate all months in range
            while (currentDate <= endDate) {
                const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
                allDates.push(monthKey);
                currentDate.setMonth(currentDate.getMonth() + 1);
            }
        } else {
            // Generate all days in range
            while (currentDate <= endDate) {
                const dayKey = currentDate.toISOString().split('T')[0];
                allDates.push(dayKey);
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        // Sort dates and create arrays with zero defaults
        const labels = allDates.map(date => {
            if (useMonthly) {
                const [year, month] = date.split('-');
                return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            }
            // Fix timezone issue by explicitly setting the date parts
            const [year, month, day] = date.split('-');
            const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        const data = allDates.map(date => dateMap.get(date) || 0);

        // Debug: Check if Nov 13 is included
        return { labels, data };
    }, [filteredTasksByPriority, filterStart, filterEnd]);

    // Prepare task status chart configuration
    useEffect(() => {
        // Only create chart config if there's data
        if (!taskMetrics || !taskMetrics.byStatus) {
            setStatusChartConfig(null);
            return;
        }

        const totalTasks = (taskMetrics.byStatus.open || 0) + (taskMetrics.byStatus.overdue || 0) + 
                          (taskMetrics.byStatus.completed || 0) + (taskMetrics.byStatus.closed || 0);

        if (totalTasks === 0) {
            setStatusChartConfig(null);
            return;
        }

        setStatusChartConfig({
            id: 'status-chart', // Add unique ID
            type: 'doughnut',
            data: {
                labels: ['Open', 'Overdue', 'Completed', 'Closed'],
                datasets: [{
                    data: [
                        taskMetrics.byStatus.open || 0,
                        taskMetrics.byStatus.overdue || 0,
                        taskMetrics.byStatus.completed || 0,
                        taskMetrics.byStatus.closed || 0
                    ],
                    backgroundColor: [
                        '#3B82F6',
                        '#EF4444',
                        '#10B981',
                        '#6B7280'
                    ],
                    borderWidth: 2,
                    borderColor: '#fff'
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Task Status Distribution',
                        font: { size: 16 }
                    },
                    legend: {
                        display: true,
                        position: 'right'
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                                return `${context.label}: ${context.parsed} (${percentage}%)`;
                            }
                        }
                    }
                }
            }
        });
    }, [taskMetrics]);

    // Prepare tasks by assignee chart configuration
    useEffect(() => {
        // Only create chart config if there's data
        if (!tasksByAssignee || tasksByAssignee.length === 0) {
            setAssigneeChartConfig(null);
            return;
        }

        setAssigneeChartConfig({
            id: 'assignee-chart', // Add unique ID
            type: 'bar',
            data: {
                labels: tasksByAssignee.map(a => a.name),
                datasets: [
                    {
                        label: 'Open',
                        data: tasksByAssignee.map(a => a.open || 0),
                        backgroundColor: '#3B82F6'
                    },
                    {
                        label: 'Overdue',
                        data: tasksByAssignee.map(a => a.overdue || 0),
                        backgroundColor: '#EF4444'
                    },
                    {
                        label: 'Completed',
                        data: tasksByAssignee.map(a => a.completed || 0),
                        backgroundColor: '#10B981'
                    },
                    {
                        label: 'Closed',
                        data: tasksByAssignee.map(a => a.closed || 0),
                        backgroundColor: '#6B7280'
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Tasks by Assignee',
                        font: { size: 16 }
                    },
                    legend: {
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    }
                },
                scales: {
                    x: {
                        stacked: true
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: {
                            precision: 0
                        }
                    }
                }
            }
        });
    }, [tasksByAssignee]);

    // Prepare completion trend chart configuration
    useEffect(() => {
        // Only create chart config if there's data
        if (!completionTrend || !completionTrend.labels || completionTrend.labels.length === 0) {
            setCompletionTrendChartConfig(null);
            return;
        }

        setCompletionTrendChartConfig({
            id: 'completion-trend-chart', // Add unique ID
            type: 'line',
            data: {
                labels: completionTrend.labels,
                datasets: [{
                    label: 'Tasks Completed',
                    data: completionTrend.data,
                    borderColor: '#10B981',
                    backgroundColor: '#10B98120',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Task Completion Trend',
                        font: { size: 16 }
                    },
                    legend: {
                        display: false
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            precision: 0
                        }
                    }
                }
            }
        });
    }, [completionTrend]);

    const exportSections = [
        { key: 'byAssignee', label: 'Tasks by Assignee' },
        { key: 'overdue', label: 'Overdue Tasks' }
    ];

    const handleExport = (format, sectionKey) => {
        const dateLabel = range === 'ytd' ? 'YTD' : `${range}d`;

        // Tasks by Assignee section
        const assigneeHeaders = ['Assignee', 'Total Tasks', 'Open', 'Overdue', 'Completed', 'Closed'];
        const assigneeRowMapper = (row) => [
            row.name || '',
            row.total || 0,
            row.open || 0,
            row.overdue || 0,
            row.completed || 0,
            row.closed || 0
        ];

        // Overdue Tasks section
        const overdueHeaders = ['Task', 'Assigned To', 'Priority', 'Due Date', 'Days Overdue'];
        const overdueRowMapper = (task) => {
            const dueDate = task.dueDate?.toDate?.() ? task.dueDate.toDate() : new Date(task.dueDate);
            const daysOverdue = Math.ceil((new Date() - dueDate) / (1000 * 60 * 60 * 24));
            return [
                task.title || task.taskName || '',
                task.assignedTo || '',
                task.priority || '',
                dueDate.toLocaleDateString(),
                daysOverdue
            ];
        };

        if (sectionKey === 'byAssignee') {
            exportData({
                filename: `task_reports_by_assignee_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: assigneeHeaders, data: tasksByAssignee, rowMapper: assigneeRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Tasks by Assignee (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'By Assignee', columnWidths: [25, 12, 10, 10, 12, 10] }
            });
        } else if (sectionKey === 'overdue') {
            exportData({
                filename: `task_reports_overdue_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: overdueHeaders, data: overdueTasks, rowMapper: overdueRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Overdue Tasks (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'Overdue Tasks', columnWidths: [30, 20, 12, 15, 12] }
            });
        } else {
            // "all" — multi-section export
            exportMultiSectionData({
                filename: `task_reports_all_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format,
                sections: [
                    { name: 'Tasks by Assignee', headers: assigneeHeaders, data: tasksByAssignee, rowMapper: assigneeRowMapper, columnWidths: [25, 12, 10, 10, 12, 10] },
                    { name: 'Overdue Tasks', headers: overdueHeaders, data: overdueTasks, rowMapper: overdueRowMapper, columnWidths: [30, 20, 12, 15, 12] }
                ],
                options: { title: `Task Reports (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
            });
        }
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading task reports...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Task Reports</h2>
                    <p className="text-gray-600">Analyze task completion rates, overdue items, and team productivity</p>
                </div>
                {can(FEATURE_PERMISSIONS.REPORTS_EXPORT) && (
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={loading || tasksByAssignee.length === 0}
                        tooltipText="Export task report data"
                        label="Export"
                        className="bg-indigo-600 hover:bg-indigo-700"
                        sections={exportSections}
                    />
                )}
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Date
                        </label>
                        <DateRangeSelector
                            range={range}
                            setRange={setRange}
                            customStart={customStart}
                            setCustomStart={setCustomStart}
                            customEnd={customEnd}
                            setCustomEnd={setCustomEnd}
                            requireApply={true}
                            defaultRange="30"
                            onClear={() => { setSelectedAssignees([]); setPendingAssignees([]); setSelectedPriorities([]); setPendingPriorities([]); }}
                        />
                    </div>

                    {/* Assignee Filter */}
                    {allAssignees.length > 0 && (
                        <div className="w-full md:w-64">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Assignee
                            </label>
                            <div className="relative" ref={assigneeDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-left flex items-center justify-between"
                                >
                                    <span className="block truncate">
                                        {selectedAssignees.length === 0 
                                            ? 'All Assignees' 
                                            : selectedAssignees.length === allAssignees.length
                                            ? 'All Assignees'
                                            : `${selectedAssignees.length} selected`}
                                    </span>
                                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>

                    {showAssigneeDropdown && (
                        <div className="absolute z-50 mt-2 w-80 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                            <div className="p-3 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-gray-700">Select Assignees</span>
                                    <button
                                        onClick={() => {
                                            if (pendingAssignees.length === allAssignees.length) {
                                                setPendingAssignees([]);
                                            } else {
                                                setPendingAssignees(allAssignees.map(a => a.name));
                                            }
                                        }}
                                        className="text-xs text-purple-600 hover:text-purple-800"
                                    >
                                        {pendingAssignees.length === allAssignees.length ? 'Clear All' : 'Select All'}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {pendingAssignees.length} of {allAssignees.length} selected
                                </p>
                            </div>
                            
                            <div className="p-2">
                                {allAssignees.map(assignee => (
                                    <label
                                        key={assignee.name}
                                        className="flex items-center px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={pendingAssignees.includes(assignee.name)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setPendingAssignees([...pendingAssignees, assignee.name]);
                                                } else {
                                                    setPendingAssignees(pendingAssignees.filter(a => a !== assignee.name));
                                                }
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">{assignee.name}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                                <button
                                    onClick={handleCancelAssigneeFilter}
                                    className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApplyAssigneeFilter}
                                    className="px-3 py-1.5 text-sm text-white bg-purple-600 rounded hover:bg-purple-700"
                                >
                                    Apply Filter
                                </button>
                            </div>
                        </div>
                    )}
                            </div>
                        </div>
                    )}

                    {/* Priority Filter */}
                    <div className="w-full md:w-48">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Priority
                        </label>
                        <div className="relative" ref={priorityDropdownRef}>
                            <button
                                type="button"
                                onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-left flex items-center justify-between"
                            >
                                <span className="block truncate">
                                    {selectedPriorities.length === 0 
                                        ? 'All Priorities' 
                                        : selectedPriorities.length === 3
                                        ? 'All Priorities'
                                        : `${selectedPriorities.length} selected`}
                                </span>
                                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {showPriorityDropdown && (
                                <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-300 rounded-lg shadow-xl">
                                    <div className="p-3 border-b border-gray-200 bg-gray-50">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-gray-700">Select Priorities</span>
                                            <button
                                                onClick={() => {
                                                    if (pendingPriorities.length === 3) {
                                                        setPendingPriorities([]);
                                                    } else {
                                                        setPendingPriorities(['High', 'Medium', 'Low']);
                                                    }
                                                }}
                                                className="text-xs text-purple-600 hover:text-purple-800"
                                            >
                                                {pendingPriorities.length === 3 ? 'Clear All' : 'Select All'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            {pendingPriorities.length} of 3 selected
                                        </p>
                                    </div>
                                    
                                    <div className="p-2">
                                        {['High', 'Medium', 'Low'].map(priority => (
                                            <label
                                                key={priority}
                                                className="flex items-center px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={pendingPriorities.includes(priority)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setPendingPriorities([...pendingPriorities, priority]);
                                                        } else {
                                                            setPendingPriorities(pendingPriorities.filter(p => p !== priority));
                                                        }
                                                    }}
                                                    className="mr-2"
                                                />
                                                <span className={`text-sm font-semibold ${
                                                    priority === 'High' ? 'text-red-600' : 
                                                    priority === 'Low' ? 'text-gray-600' : 
                                                    'text-yellow-600'
                                                }`}>
                                                    {priority === 'High' ? '🔴' : priority === 'Low' ? '🟢' : '🟡'} {priority}
                                                </span>
                                            </label>
                                        ))}
                                    </div>

                                    <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                                        <button
                                            onClick={handleCancelPriorityFilter}
                                            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleApplyPriorityFilter}
                                            className="px-3 py-1.5 text-sm text-white bg-purple-600 rounded hover:bg-purple-700"
                                        >
                                            Apply Filter
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={handleClearFilters}
                            className="px-4 py-2 rounded text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-300 hover:border-red-300 transition-colors"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Empty state */}
            {tasks.length === 0 && (
                <div className="bg-white p-12 rounded-lg shadow text-center">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Tasks Found</h3>
                    <p className="text-gray-600 mb-4">
                        The tasks collection doesn't exist yet or is empty. Tasks will appear here once created.
                    </p>
                </div>
            )}

            {tasks.length > 0 && (
                <>
                    {/* Task Metrics Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
                        <Tooltip text="Total number of tasks created in the selected date range.">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-xs text-gray-600 mb-1">Total Tasks</div>
                                <div className="text-2xl font-bold text-purple-600">{taskMetrics.total}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="Tasks currently open and awaiting completion (excluding overdue tasks).">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-xs text-gray-600 mb-1">Open Tasks</div>
                                <div className="text-2xl font-bold text-blue-600">{taskMetrics.byStatus.open || 0}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="Open tasks past their due date that need immediate attention.">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-xs text-gray-600 mb-1">Overdue</div>
                                <div className="text-2xl font-bold text-red-600">{taskMetrics.overdue}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="Tasks marked as completed in the selected period.">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-xs text-gray-600 mb-1">Completed</div>
                                <div className="text-2xl font-bold text-green-600">{taskMetrics.byStatus.completed || 0}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="Tasks that have been closed without completion.">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-xs text-gray-600 mb-1">Closed</div>
                                <div className="text-2xl font-bold text-gray-600">{taskMetrics.byStatus.closed || 0}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="High priority tasks requiring immediate attention.">
                            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                                <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                    🔴 High Priority
                                </div>
                                <div className="text-2xl font-bold text-red-600">{taskMetrics.byPriority.High || 0}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="Medium priority tasks - standard workflow items.">
                            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                                <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                    🟡 Medium Priority
                                </div>
                                <div className="text-2xl font-bold text-yellow-600">{taskMetrics.byPriority.Medium || 0}</div>
                            </div>
                        </Tooltip>

                        <Tooltip text="Low priority tasks - can be addressed when time permits.">
                            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-gray-400">
                                <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                    🟢 Low Priority
                                </div>
                                <div className="text-2xl font-bold text-gray-600">{taskMetrics.byPriority.Low || 0}</div>
                            </div>
                        </Tooltip>
                    </div>

                    {/* Overdue Tasks Table - Moved to top for visibility */}
                    {overdueTasks.length > 0 && (
                        <div className="bg-white p-6 rounded-lg shadow mb-6">
                            <Tooltip text="Tasks that are past their due date and still open. These require immediate attention.">
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                    <span className="text-red-600 mr-2">⚠️</span>
                                    Overdue Tasks ({overdueTasks.length})
                                </h3>
                            </Tooltip>
                            
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task #</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Overdue</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {overdueTasks.slice(0, 20).map(task => {
                                            const dueDate = task.dueDate?.toDate?.() 
                                                ? task.dueDate.toDate() 
                                                : new Date(task.dueDate);
                                            const now = new Date();
                                            const daysOverdue = Math.ceil((now - dueDate) / (1000 * 60 * 60 * 24));

                                            return (
                                                <tr key={task.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm font-mono">
                                                        <Link to={`/tasks/${task.id}`} className="text-indigo-600 hover:underline">
                                                            {task.taskNumberLabel || task.id}
                                                        </Link>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{task.title || 'Untitled Task'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{task.assignedTo || 'Unassigned'}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                            task.priority === 'High' 
                                                                ? 'bg-red-100 text-red-800'
                                                                : task.priority === 'Low'
                                                                    ? 'bg-gray-100 text-gray-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                            {task.priority || 'Medium'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(dueDate)}</td>
                                                    <td className="px-4 py-3 text-sm font-bold text-red-600">{daysOverdue} day{daysOverdue !== 1 ? 's' : ''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Charts Section */}
                    <div className="space-y-6 mb-6">
                        {/* First Row: Tasks by Status and Task Completion Trend */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Task Status Chart */}
                            <div className="w-full">
                                {statusChartConfig ? (
                                    <CompactChart
                                        chartConfig={statusChartConfig}
                                        title="Tasks by Status"
                                        height="h-64"
                                        tooltip="Distribution of tasks by current status. Shows what percentage are open, completed, or closed."
                                    />
                                ) : (
                                    <div className="bg-white p-6 rounded-lg shadow">
                                        <h3 className="text-xl font-bold text-gray-900 mb-4">Tasks by Status</h3>
                                        <div className="h-64 flex items-center justify-center">
                                            <div className="text-gray-500 text-center">
                                                <div className="text-4xl mb-2">📋</div>
                                                <div>No task data available</div>
                                                <div className="text-sm mt-1">Tasks will appear here once created or change filters</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Task Completion Trend Chart */}
                            <div className="w-full">
                                {completionTrendChartConfig ? (
                                    <CompactChart
                                        chartConfig={completionTrendChartConfig}
                                        title="Task Completion Trend"
                                        height="h-64"
                                        tooltip="Shows task completion trends over time. Higher is better!"
                                    />
                                ) : (
                                    <div className="bg-white p-6 rounded-lg shadow">
                                        <h3 className="text-xl font-bold text-gray-900 mb-4">Task Completion Trend</h3>
                                        <div className="h-64 flex items-center justify-center">
                                            <div className="text-gray-500 text-center">
                                                <div className="text-4xl mb-2">📈</div>
                                                <div>No completion data available</div>
                                                <div className="text-sm mt-1">Complete tasks to see trends or change filters</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tasks by Assignee - Separated Section */}
                    <div className="mb-6">
                        {assigneeChartConfig ? (
                            <CompactChart
                                chartConfig={assigneeChartConfig}
                                title="Tasks by Assignee"
                                height="h-96"
                                tooltip="Stacked bar chart showing task distribution by team member. Each color represents a task status."
                            />
                        ) : (
                            <div className="bg-white p-6 rounded-lg shadow">
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Tasks by Assignee</h3>
                                <div className="h-96 flex items-center justify-center">
                                    <div className="text-gray-500 text-center">
                                        <div className="text-4xl mb-2">👥</div>
                                        <div>No assignee data available</div>
                                        <div className="text-sm mt-1">Assign tasks to team members to see this chart or change filters</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </main>
    );
};

export default TaskReportsPage;
